import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import purchaseOrderService from '../services/purchaseOrders';
import sprayService from '../services/spray';

interface PurchaseOrderAccessResult {
  /** Authorised by an Accounts Admin, or a Retail Manager: raise, send and receive orders. */
  canRaise: boolean;
  /** Raises orders, or holds a Retail seat: receives deliveries. */
  canReceive: boolean;
  /** Authorised, or holding an Accounts seat. */
  canRead: boolean;
  /** An Accounts seat: adds and edits the organisation's suppliers. */
  canEditSuppliers: boolean;
  loading: boolean;
}

/**
 * What this person can do with purchase orders in the organisation the tab is
 * showing. The answer is the server's (`GET /purchase-orders/access`): the
 * Accounts Admin authorises people on Accounts → Who raises purchase orders, and
 * an Accounts seat reads orders and keeps the suppliers. A failed check reads
 * as no access; the server refuses regardless.
 */
export function usePurchaseOrderAccess(): PurchaseOrderAccessResult {
  const { user } = useAuth();
  const [state, setState] = useState<PurchaseOrderAccessResult>({
    canRaise: false, canReceive: false, canRead: false, canEditSuppliers: false, loading: true,
  });

  useEffect(() => {
    let cancelled = false;
    const done = (canRaise: boolean, canRead: boolean, canEditSuppliers = false, canReceive = canRaise) => {
      if (!cancelled) setState({ canRaise, canReceive, canRead, canEditSuppliers, loading: false });
    };
    if (!user?.organisation_id) {
      done(false, false);
    } else if (sprayService.getTargetOrgId() !== null) {
      // A spray advisor acting for another organisation: that organisation's
      // advisor record decides, on each request. Its suppliers are its own.
      done(true, true);
    } else {
      purchaseOrderService.getAccess()
        .then((res) => {
          const d = res.success ? res.data : undefined;
          // An older server sends no can_receive: whoever raises receives.
          done(Boolean(d?.can_raise), Boolean(d?.can_read), Boolean(d?.can_edit_suppliers),
            Boolean(d?.can_receive ?? d?.can_raise));
        })
        .catch(() => done(false, false));
    }
    return () => { cancelled = true; };
  }, [user?.organisation_id]);

  return state;
}
